import type {MembershipPassCard as MembershipPassCardType} from '@cohort/components-xps/components/cards/MembershipPassCard';
import MembershipPassCard from '@cohort/components-xps/components/cards/MembershipPassCard';
import {getImageUrl, Sizes} from '@cohort/shared/utils/media';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import {AboutMerchant} from '@cohort/wallet/components/space/AboutMerchant';
import {Achievements} from '@cohort/wallet/components/space/Achievements';
import {useUserLogin} from '@cohort/wallet/hooks/api/User';
import {useAppStore} from '@cohort/wallet/hooks/stores/app';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {logoutUser} from '@cohort/wallet/lib/Authentication';
import {getMerchantSpaceLoginRoute, getSpaceSettingsRoute} from '@cohort/wallet/lib/Pages';
import {Power} from '@phosphor-icons/react';
import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

export const AboutBrand: React.FC = () => {
  const {space} = useSpaceContext();
  const {isMobile} = useScreenSizeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation('components', {keyPrefix: 'space.aboutBrand'});
  const {accentColor} = useThemeContext();
  const {data: user} = useUserLogin();
  const disableLogout = useAppStore(store => store.disableLogout);

  if (!space || !user) {
    return <Fragment />; // Replace me with a skeleton.
  }

  const membershipPassConfiguration = space.merchant.membershipPassConfiguration;

  const {membershipPass} = user;
  const membershipPassCard = {
    title: membershipPassConfiguration.title,
    imageUrl: membershipPassConfiguration.bannerFileKey
      ? getImageUrl(import.meta.env.COHORT_ENV, membershipPassConfiguration.bannerFileKey, {
          h: Sizes.M,
          w: Sizes.M,
        })
      : null,
    backgroundColorCode: membershipPassConfiguration.backgroundColorCode,
    experienceSpaceBackgroundColorCode: membershipPassConfiguration.backgroundColorCode,
    memberHandle: membershipPass.memberHandle,
    subscriptionDate: membershipPass.subscriptionDate,
    memberId: membershipPass.memberId,
  } satisfies MembershipPassCardType;

  return (
    <div className="flex h-full w-full flex-grow-0 flex-col space-y-6 md:space-y-8">
      <MembershipPassCard membershipPassCard={membershipPassCard} />
      {isMobile ? (
        <Fragment>
          <AboutMerchant />
          <Achievements />
        </Fragment>
      ) : (
        <Fragment>
          <Achievements />
          <AboutMerchant />
          {location.pathname === getSpaceSettingsRoute() && !disableLogout && (
            <div className="mt-auto flex grow flex-col justify-end">
              <SecondaryButton
                text={
                  <div className="flex items-center gap-2">
                    <Power size={16} style={{color: accentColor}} />
                    {t('logout')}
                  </div>
                }
                onClick={async () => {
                  await logoutUser();
                  navigate({
                    pathname: getMerchantSpaceLoginRoute(),
                    search: location.search,
                  });
                }}
                testId="about-brand-logout-btn"
                type="button"
                tracking={{namespace: 'settings.logout'}}
              />
            </div>
          )}
        </Fragment>
      )}
    </div>
  );
};
